import { useNavigate } from "react-router-dom";
import { FC, useCallback, useRef, useState } from "react";
import cn from "classnames";
import {
  Button,
  Container,
  DropAcceptedFunc,
  FileItem,
  FileUpload,
  notification,
  Nullable,
  useWillUnmount,
  useDidUpdate,
  useFileQueue,
} from "@epcnetwork/core-ui-kit";

import { getAcceptedFilesText, getPipelineRoute, getSupportedFormats, onFileRedirect } from "utils";
import { useTracking } from "hooks";
import { SMARTLOOK_EVENTS } from "constants/smartlook.constants";
import { PIPELINES_LIST_PAGE } from "constants/routes.constants";
import { postNewSuppressionFile } from "api";
import { PipelineHeaderSection } from "../components/pipeline-header-section/pipeline-header-section";
import {
  SuppressionFileItem,
  PipelineSuppressionData,
  SuppressionFileInfo,
} from "./suppression.types";
import { supportedFormats, getInitialData } from "./suppression.constants";
import { SuppressionFormModal } from "./form-modal/suppression-form-modal";

import styles from "./suppression.module.css";

const SuppressionPage: FC = () => {
  const componentIsMounted = useRef(true);
  const navigate = useNavigate();

  const { pipelineSubmitEvent } = useTracking();

  const pipeline = getPipelineRoute("Suppression");

  const {
    files,
    createInitialFile,
    addFiles,
    updateFiles,
    removeFiles,
    clearEntity,
    getItem,
    isEntityInConfiguration,
    isEntityFinished,
    hasAllConfigured,
    submitOneByOne,
    hasAllSuccessFiles,
  } = useFileQueue<SuppressionFileItem, PipelineSuppressionData>(
    pipeline.title,
    postNewSuppressionFile,
  );

  useWillUnmount(() => {
    componentIsMounted.current = false;
  });

  useDidUpdate(() => {
    if (!isEntityFinished) return;

    handleRedirect();
  }, [isEntityFinished]);

  const [editedFile, setEditedFile] = useState<Nullable<SuppressionFileItem>>(null);

  const handleAcceptedDrop: DropAcceptedFunc = useCallback(
    (acceptedFiles) => {
      addFiles(
        acceptedFiles.map((file) => createInitialFile(file, { data: getInitialData(file) })),
      );
    },
    [addFiles, createInitialFile],
  );

  const handleClearClick = () => {
    notification.confirm(
      "Delete files",
      `Are you sure you want to delete all ${files.length} file(s)?`,
      {
        onOk: clearEntity,
        icon: "delete",
      },
    );
  };

  const handleConfigureItem = useCallback(
    (id: string) => {
      const item = getItem(id);
      if (!item) return;
      setEditedFile(item);
    },
    [getItem],
  );

  const handleModalSubmit = useCallback(
    ({ id, ...rest }: SuppressionFileItem) => {
      updateFiles({ id, file: rest });
      setEditedFile(null);
    },
    [updateFiles],
  );

  const handleCloseModal = useCallback(() => setEditedFile(null), []);

  const handleSubmit = () => {
    const data = files.map((file) => {
      const formData = new FormData();

      const { fileName, emailIndex, hasHeaders, listIds } = file.data;
      const suppressionData: SuppressionFileInfo = {
        emailIndex,
        hasHeaders,
        listIds,
      };

      formData.append("fileInfo", JSON.stringify(suppressionData));
      formData.append("file", file.originalFile, fileName);

      return {
        id: file.id,
        data: formData,
      };
    });

    submitOneByOne(data);
    pipelineSubmitEvent(SMARTLOOK_EVENTS.suppressionSubmit, files);
  };

  const handleRedirect = () => {
    onFileRedirect({ files, clearEntity, navigate });
  };

  const handleRedirectToPipelines = () => {
    navigate(PIPELINES_LIST_PAGE.path);
  };

  return (
    <Container>
      <div className={styles.container}>
        <PipelineHeaderSection pipeline={pipeline} />

        <div className={styles.form}>
          <FileUpload
            className={cn(styles.dropZone, { [styles.uploadZone]: files.length })}
            uploadedFilesLength={files.length}
            subtitle={getAcceptedFilesText(getSupportedFormats(supportedFormats))}
            accept={supportedFormats}
            onDropAccepted={handleAcceptedDrop}
            exceedFilesOption="splice-with-error"
            disabled={!isEntityInConfiguration}
            preventDropOnDocument
            multiple
          />
          <div className={styles.fileList}>
            {files.map(({ id, originalFile, data, ...rest }) => {
              const showFileContent = data.emailIndex >= 0;

              return (
                <FileItem
                  {...rest}
                  key={id}
                  id={id}
                  file={originalFile}
                  onCrossClick={removeFiles}
                  onSetValuesClick={handleConfigureItem}
                  onEditValuesClick={handleConfigureItem}
                >
                  {showFileContent && (
                    <div className={styles.column}>
                      {data.emailIndex >= 0 && (
                        <div className={styles.additionalInfo}>
                          <span>Selected email column:</span> {data.emailIndex + 1}
                        </div>
                      )}
                    </div>
                  )}
                </FileItem>
              );
            })}
          </div>
          {editedFile && (
            <SuppressionFormModal
              file={editedFile}
              setClose={handleCloseModal}
              onSubmitClick={handleModalSubmit}
            />
          )}
          <div className={styles.buttons}>
            <Button
              appearance="secondary"
              onClick={!files.length ? handleRedirectToPipelines : handleClearClick}
              disabled={isEntityFinished && !hasAllSuccessFiles}
            >
              Cancel
            </Button>
            {!isEntityFinished && (
              <Button
                onClick={handleSubmit}
                disabled={!hasAllConfigured || !files.length}
                loading={!isEntityInConfiguration}
              >
                Submit
              </Button>
            )}
          </div>
        </div>
      </div>
    </Container>
  );
};

export { SuppressionPage };
